import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { normalizeLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n'

/** Short label shown on the control. A language name is not itself translated. */
const SHORT_LABEL: Record<SupportedLanguage, string> = { nb: 'NO', en: 'EN' }
const NAME_KEY = { nb: 'language.nb', en: 'language.en' } as const

type LanguageSwitcherProps = {
  className?: string
  /** Full-width, larger hit areas — used inside the mobile sheet. */
  block?: boolean
}

/**
 * Two-state segmented control. Implemented as buttons with `aria-pressed`
 * rather than a select, so the current language is announced and both options
 * are one tab-stop away.
 */
export const LanguageSwitcher = ({ className, block = false }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation()
  const current = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language)

  return (
    <div
      role="group"
      aria-label={t('language.label')}
      className={cn(
        'inline-flex items-center rounded-full border border-border bg-card p-1',
        block && 'w-full',
        className,
      )}
    >
      {SUPPORTED_LANGUAGES.map((language) => {
        const active = language === current

        return (
          <button
            key={language}
            type="button"
            aria-pressed={active}
            onClick={() => void i18n.changeLanguage(language)}
            className={cn(
              'touch-target inline-flex items-center justify-center rounded-full px-3 type-body-sm font-semibold',
              'transition-colors duration-hover ease-out',
              block && 'flex-1',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <span aria-hidden="true">{SHORT_LABEL[language]}</span>
            <span className="sr-only">{t(NAME_KEY[language])}</span>
          </button>
        )
      })}
    </div>
  )
}
